import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  LogOut, Plus, Pencil, Trash2, ArrowLeft, Upload, X, Link2,
  GripVertical, Inbox, CalendarClock, CheckCircle2, RotateCcw, Phone, Pin,
} from "lucide-react";
import { API, fmtPrice, fmtKm, CATEGORIES, FUELS } from "@/lib/site";
import { LegalStrip } from "@/components/Footer";

const MAX_PHOTOS = 10;

const EMPTY_CAR = {
  brand: "",
  model: "",
  category: "Usato Garantito",
  year: new Date().getFullYear(),
  km: 0,
  price: 0,
  fuel: "Benzina",
  transmission: "Manuale",
  power: "",
  image: "",
  images: [],
  description: "",
  pinned: false,
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("manno_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cars, setCars] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("cars");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CAR);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileInputRef = useRef(null);
  const dragIdx = useRef(null);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadCars = useCallback(() => {
    fetch(`${API}/cars`)
      .then((r) => r.json())
      .then((d) => setCars(Array.isArray(d) ? d : []))
      .catch(() => toast.error("Errore nel caricamento del catalogo"));
  }, []);

  const loadRequests = useCallback(() => {
    fetch(`${API}/requests`, { headers: { Authorization: `Bearer ${localStorage.getItem("manno_token")}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRequests(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401) {
          localStorage.removeItem("manno_token");
          setToken("");
          toast.error("Sessione scaduta, accedi di nuovo");
        } else {
          loadCars();
          loadRequests();
        }
      })
      .catch(() => {
        loadCars();
        loadRequests();
      });
  }, [token, loadCars, loadRequests]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Accesso non riuscito");
      localStorage.setItem("manno_token", d.token);
      setToken(d.token);
      toast.success("Benvenuto nell'area riservata");
    } catch (err) {
      setError(err.message);
    }
  };

  const logout = () => {
    localStorage.removeItem("manno_token");
    setToken("");
  };

  const openForm = (car) => {
    if (car) {
      const images = car.images && car.images.length ? [...car.images] : car.image ? [car.image] : [];
      setForm({ ...car, images, image: images[0] || "" });
    } else {
      setForm(EMPTY_CAR);
    }
    setUrlInput("");
    setEditing(car ? car.id : "new");
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((form.images || []).length >= MAX_PHOTOS) {
      toast.error(`Massimo ${MAX_PHOTOS} foto per veicolo`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await r.json();
      if (r.status === 401) {
        toast.error("Sessione scaduta, accedi di nuovo");
        logout();
        return;
      }
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Caricamento non riuscito");
      setForm((prev) => ({ ...prev, images: [...(prev.images || []), d.url] }));
      toast.success(`Foto ${(form.images || []).length + 1} di ${MAX_PHOTOS} caricata`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addImageUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    if ((form.images || []).length >= MAX_PHOTOS) {
      toast.error(`Massimo ${MAX_PHOTOS} foto per veicolo`);
      return;
    }
    setForm((prev) => ({ ...prev, images: [...(prev.images || []), url] }));
    setUrlInput("");
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== idx) }));
  };

  const onDropSlot = (toIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === toIdx) return;
    setForm((prev) => {
      const arr = [...(prev.images || [])];
      const [moved] = arr.splice(from, 1);
      arr.splice(toIdx, 0, moved);
      return { ...prev, images: arr };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const images = (form.images || []).filter(Boolean);
    if (images.length === 0) {
      toast.error("Aggiungi almeno una foto del veicolo");
      return;
    }
    const payload = {
      ...form,
      images,
      image: images[0],
      year: Number(form.year),
      km: Number(form.km),
      price: Number(form.price),
    };
    delete payload.id;
    delete payload.created_at;
    const isNew = editing === "new";
    const r = await fetch(isNew ? `${API}/cars` : `${API}/cars/${editing}`, {
      method: isNew ? "POST" : "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (r.ok) {
      toast.success(isNew ? "Veicolo aggiunto al catalogo" : "Veicolo aggiornato");
      setEditing(null);
      loadCars();
    } else if (r.status === 401) {
      toast.error("Sessione scaduta, accedi di nuovo");
      logout();
    } else {
      toast.error("Salvataggio non riuscito");
    }
  };

  const remove = async (car) => {
    if (!window.confirm(`Eliminare ${car.brand} ${car.model} dal catalogo?`)) return;
    const r = await fetch(`${API}/cars/${car.id}`, { method: "DELETE", headers });
    if (r.ok) {
      toast.success("Veicolo eliminato");
      loadCars();
    } else {
      toast.error("Eliminazione non riuscita");
    }
  };

  const togglePin = async (car) => {
    const r = await fetch(`${API}/cars/${car.id}/pin`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ pinned: !car.pinned }),
    });
    if (r.ok) {
      toast.success(car.pinned ? "Annuncio rimosso dalla cima del catalogo" : "Annuncio fissato in cima al catalogo");
      loadCars();
    } else if (r.status === 401) {
      toast.error("Sessione scaduta, accedi di nuovo");
      logout();
    } else {
      toast.error("Operazione non riuscita");
    }
  };

  const toggleStatus = async (reqItem) => {
    const next = reqItem.status === "nuova" ? "gestita" : "nuova";
    const r = await fetch(`${API}/requests/${reqItem.id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ status: next }),
    });
    if (r.ok) {
      toast.success(next === "gestita" ? "Richiesta segnata come gestita" : "Richiesta riaperta");
      loadRequests();
    } else {
      toast.error("Operazione non riuscita");
    }
  };

  const deleteRequest = async (reqItem) => {
    if (!window.confirm(`Eliminare la richiesta di ${reqItem.name}?`)) return;
    const r = await fetch(`${API}/requests/${reqItem.id}`, { method: "DELETE", headers });
    if (r.ok) {
      toast.success("Richiesta eliminata");
      loadRequests();
    } else {
      toast.error("Eliminazione non riuscita");
    }
  };

  const input =
    "w-full bg-[#0D1119] border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500/60";

  const photos = form.images || [];
  const newCount = requests.filter((r) => r.status === "nuova").length;

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0D14] flex flex-col" data-testid="admin-login-page">
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-sm">
            <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-amber-500 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </Link>
            <form onSubmit={login} className="bg-[#111622] border border-border rounded-2xl p-8" data-testid="admin-login-form">
              <img src="/logo.png" alt="Manno Auto S.r.l." className="h-12 w-auto mb-5" data-testid="admin-login-logo" />
              <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Area Riservata</h1>
              <p className="text-sm text-gray-500 mt-1 mb-6">Gestione catalogo {`Manno Auto`}</p>
              <div className="space-y-4">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className={input}
                />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className={input}
                />
                {error && (
                  <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
                )}
                <button
                  data-testid="admin-login-submit"
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold py-3 rounded-lg transition-colors"
                >
                  Accedi
                </button>
              </div>
            </form>
          </div>
        </div>
        <LegalStrip />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0D14] text-gray-100 flex flex-col" data-testid="admin-dashboard">
      <header className="border-b border-border bg-[#111622]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Manno Auto S.r.l." className="h-7 w-auto" />
            <span className="font-mono2 text-[10px] uppercase tracking-widest text-gray-500">/ Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="text-sm text-gray-400 hover:text-amber-500 transition-colors">
              Vedi sito
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="admin-tabs">
          <button
            data-testid="admin-tab-cars"
            onClick={() => setTab("cars")}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-all ${
              tab === "cars"
                ? "bg-amber-500 text-[#0A0D14] border-amber-500"
                : "border-border text-gray-400 hover:border-amber-500/50 hover:text-gray-200"
            }`}
          >
            Catalogo
          </button>
          <button
            data-testid="admin-tab-requests"
            onClick={() => setTab("requests")}
            className={`relative px-5 py-2 rounded-full text-sm font-medium border transition-all inline-flex items-center gap-2 ${
              tab === "requests"
                ? "bg-amber-500 text-[#0A0D14] border-amber-500"
                : "border-border text-gray-400 hover:border-amber-500/50 hover:text-gray-200"
            }`}
          >
            <Inbox className="w-4 h-4" />
            Richieste
            {newCount > 0 && (
              <span
                data-testid="admin-requests-badge"
                className={`text-[10px] font-mono2 px-2 py-0.5 rounded-full ${
                  tab === "requests" ? "bg-[#0A0D14] text-amber-500" : "bg-amber-500 text-[#0A0D14]"
                }`}
              >
                {newCount}
              </span>
            )}
          </button>
        </div>

        {tab === "cars" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Catalogo veicoli</h1>
                <p className="text-sm text-gray-500 mt-1" data-testid="admin-car-count">{cars.length} veicoli pubblicati</p>
              </div>
              <button
                data-testid="admin-add-car-btn"
                onClick={() => openForm(null)}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold px-5 py-2.5 rounded-full transition-colors"
              >
                <Plus className="w-4 h-4" /> Aggiungi veicolo
              </button>
            </div>

            <div className="grid gap-4">
              {cars.map((car) => (
                <div
                  key={car.id}
                  data-testid={`admin-car-row-${car.id}`}
                  className={`bg-[#111622] border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${
                    car.pinned ? "border-amber-500/50" : "border-border"
                  }`}
                >
                  <img src={car.image} alt="" className="w-full sm:w-28 h-40 sm:h-20 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-amber-500 flex items-center gap-2">
                      {car.brand} · {car.category}
                      {car.pinned && (
                        <span
                          className="inline-flex items-center gap-1 bg-amber-500/15 border border-amber-500/40 text-amber-500 px-2 py-0.5 rounded-full"
                          data-testid={`admin-pinned-badge-${car.id}`}
                        >
                          <Pin className="w-3 h-3" /> Fissato in alto
                        </span>
                      )}
                    </p>
                    <p className="font-display text-lg font-bold truncate">{car.model}</p>
                    <p className="text-xs text-gray-500">
                      {car.year} · {fmtKm(car.km)} · {car.fuel}
                      {car.images && car.images.length > 1 && ` · ${car.images.length} foto`}
                    </p>
                  </div>
                  <p className="font-display text-xl font-extrabold text-amber-500 shrink-0">{fmtPrice(car.price)}</p>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`admin-pin-car-${car.id}`}
                      onClick={() => togglePin(car)}
                      className={`p-2.5 rounded-lg border transition-colors ${
                        car.pinned
                          ? "border-amber-500/60 bg-amber-500/15 text-amber-500"
                          : "border-border text-gray-400 hover:text-amber-500 hover:border-amber-500/40"
                      }`}
                      aria-label={car.pinned ? "Rimuovi dalla cima del catalogo" : "Fissa in cima al catalogo"}
                      title={car.pinned ? "Rimuovi dalla cima del catalogo" : "Fissa in cima al catalogo"}
                    >
                      <Pin className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`admin-edit-car-${car.id}`}
                      onClick={() => openForm(car)}
                      className="p-2.5 rounded-lg border border-border text-gray-400 hover:text-amber-500 hover:border-amber-500/40 transition-colors"
                      aria-label="Modifica"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`admin-delete-car-${car.id}`}
                      onClick={() => remove(car)}
                      className="p-2.5 rounded-lg border border-border text-gray-400 hover:text-red-400 hover:border-red-500/40 transition-colors"
                      aria-label="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "requests" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Richieste clienti</h1>
                <p className="text-sm text-gray-500 mt-1" data-testid="admin-requests-count">
                  {requests.length} richieste · {newCount} da gestire
                </p>
              </div>
              <button
                data-testid="admin-requests-refresh"
                onClick={loadRequests}
                className="inline-flex items-center gap-2 border border-border text-gray-400 hover:text-amber-500 hover:border-amber-500/40 text-sm px-4 py-2.5 rounded-full transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Aggiorna
              </button>
            </div>

            {requests.length === 0 && (
              <div className="text-center py-20 text-gray-500" data-testid="admin-requests-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="font-display text-xl uppercase font-bold">Nessuna richiesta</p>
                <p className="mt-2 text-sm">Le richieste di test drive e i messaggi dal modulo contatti appariranno qui.</p>
              </div>
            )}

            <div className="grid gap-4">
              {requests.map((reqItem) => (
                <div
                  key={reqItem.id}
                  data-testid={`admin-request-row-${reqItem.id}`}
                  className={`bg-[#111622] border rounded-xl p-5 ${
                    reqItem.status === "nuova" ? "border-amber-500/40" : "border-border opacity-75"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                            reqItem.type === "test_drive"
                              ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                              : "text-blue-300 border-blue-500/40 bg-blue-500/10"
                          }`}
                          data-testid={`admin-request-type-${reqItem.id}`}
                        >
                          {reqItem.type === "test_drive" ? "Test Drive" : "Contatto"}
                        </span>
                        <span
                          className={`text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                            reqItem.status === "nuova"
                              ? "text-red-400 border-red-500/40 bg-red-500/10"
                              : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                          }`}
                          data-testid={`admin-request-status-${reqItem.id}`}
                        >
                          {reqItem.status === "nuova" ? "Da gestire" : "Gestita"}
                        </span>
                        <span className="text-[11px] text-gray-600 font-mono2">{fmtDate(reqItem.created_at)}</span>
                      </div>
                      <p className="mt-2.5 font-display text-lg font-bold">
                        {reqItem.name}
                        <a
                          href={`tel:${reqItem.phone.replace(/\s/g, "")}`}
                          className="ml-3 inline-flex items-center gap-1.5 text-sm font-normal text-amber-500 hover:text-amber-400"
                          data-testid={`admin-request-phone-${reqItem.id}`}
                        >
                          <Phone className="w-3.5 h-3.5" /> {reqItem.phone}
                        </a>
                      </p>
                      {reqItem.car_label && (
                        <p className="text-sm text-gray-400 mt-1">
                          <CalendarClock className="w-3.5 h-3.5 inline mr-1.5 text-gray-500" />
                          {reqItem.car_label}
                          {reqItem.preferred_date && ` · data preferita: ${reqItem.preferred_date}`}
                        </p>
                      )}
                      {reqItem.message && (
                        <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">“{reqItem.message}”</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`admin-request-toggle-${reqItem.id}`}
                        onClick={() => toggleStatus(reqItem)}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 rounded-lg border transition-colors ${
                          reqItem.status === "nuova"
                            ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-white"
                            : "border-border text-gray-400 hover:text-amber-500 hover:border-amber-500/40"
                        }`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {reqItem.status === "nuova" ? "Segna gestita" : "Riapri"}
                      </button>
                      <button
                        data-testid={`admin-request-delete-${reqItem.id}`}
                        onClick={() => deleteRequest(reqItem)}
                        className="p-2.5 rounded-lg border border-border text-gray-400 hover:text-red-400 hover:border-red-500/40 transition-colors"
                        aria-label="Elimina richiesta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <LegalStrip />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111622] border-border text-gray-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-car-form-modal">
          <DialogTitle className="font-display text-2xl font-extrabold uppercase tracking-tight mb-4">
            {editing === "new" ? "Nuovo veicolo" : "Modifica veicolo"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Modulo per {editing === "new" ? "aggiungere un nuovo veicolo al" : "modificare un veicolo del"} catalogo
          </DialogDescription>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="admin-car-form">
            <input data-testid="car-form-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marca *" className={input} />
            <input data-testid="car-form-model" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Modello *" className={input} />
            <select data-testid="car-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="car-form-fuel" value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })} className={input}>
              {FUELS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input data-testid="car-form-year" required type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Anno *" className={input} />
            <input data-testid="car-form-km" required type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} placeholder="Chilometri *" className={input} />
            <input data-testid="car-form-price" required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prezzo € *" className={input} />
            <input data-testid="car-form-power" required value={form.power} onChange={(e) => setForm({ ...form, power: e.target.value })} placeholder="Potenza (es. 150 CV) *" className={input} />
            <select data-testid="car-form-transmission" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} className={input}>
              <option>Manuale</option>
              <option>Automatico</option>
            </select>

            <div className="sm:col-span-2" data-testid="car-form-photo-section">
              <p className="text-xs text-gray-500 mb-2">
                Foto del veicolo * — {photos.length}/{MAX_PHOTOS} · trascina per riordinare, la prima è la copertina
              </p>
              <div className="grid grid-cols-3 gap-2.5">
                {photos.map((src, i) => (
                  <div
                    key={`${src}-${i}`}
                    draggable
                    onDragStart={() => (dragIdx.current = i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropSlot(i)}
                    className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-border bg-[#0D1119] cursor-grab active:cursor-grabbing hover:border-amber-500/50 transition-colors"
                    data-testid={`car-form-photo-slot-${i}`}
                  >
                    <img src={src} alt={`Foto ${i + 1}`} className="w-full h-full object-cover pointer-events-none" draggable={false} />
                    {i === 0 && (
                      <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono2 uppercase tracking-widest bg-amber-500 text-[#0A0D14] px-2 py-0.5 rounded-full">
                        Copertina
                      </span>
                    )}
                    <span className="absolute top-1.5 left-1.5 p-1 rounded bg-black/50 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-3.5 h-3.5" />
                    </span>
                    <button
                      type="button"
                      data-testid={`car-form-remove-photo-${i}`}
                      onClick={() => removeImage(i)}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/60 border border-border text-gray-300 hover:text-red-400 hover:border-red-500/50 transition-colors"
                      aria-label={`Rimuovi foto ${i + 1}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    data-testid="car-form-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-[4/3] rounded-lg border border-dashed border-border hover:border-amber-500/60 flex flex-col items-center justify-center gap-1.5 text-gray-500 hover:text-amber-500 transition-colors disabled:opacity-50"
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-[10px] font-mono2 uppercase tracking-widest">
                      {uploading ? "Caricamento…" : "Aggiungi foto"}
                    </span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                data-testid="car-form-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={uploadPhoto}
                className="hidden"
              />
              <div className="mt-3 flex gap-2">
                <input
                  data-testid="car-form-add-url-input"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="…oppure incolla URL immagine"
                  className={`${input} flex-1`}
                />
                <button
                  type="button"
                  data-testid="car-form-add-url-btn"
                  onClick={addImageUrl}
                  className="inline-flex items-center gap-2 bg-[#1E273A] hover:bg-amber-500 hover:text-[#0A0D14] border border-border rounded-lg px-4 text-sm font-medium transition-colors"
                >
                  <Link2 className="w-4 h-4" /> Aggiungi
                </button>
              </div>
            </div>

            <textarea data-testid="car-form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrizione" className={`${input} sm:col-span-2`} />
            <div className="sm:col-span-2 flex gap-3 justify-end">
              <button type="button" data-testid="car-form-cancel" onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-lg border border-border text-sm text-gray-400 hover:text-gray-200 transition-colors">
                Annulla
              </button>
              <button type="submit" data-testid="car-form-save" className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-[#0A0D14] text-sm font-semibold transition-colors">
                Salva veicolo
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
